<template>
	<div
		:class="{ '-multi': isInMulti, 'is-playing': isPlaying }"
		class="tag-preview"
		@mouseenter="handleMouseEnter"
		@mouseleave="handleMouseLeave"
	>
		<div
			class="tag-preview__item"
			@click.stop="handlePlayToggle"
		>
			<div
				class="tag-preview__time-play-w"
				:class="{ '-game-minute': gameMinute }"
			>
				<Icon
					v-if="!isPlaying || previewFinished"
					class="tag-preview__play-icon"
					icon-name="button-play-filled"
					size="sm"
				/>
				<Icon
					v-else
					class="tag-preview__play-icon"
					icon-name="button-pause-filled"
					size="sm"
				/>
				<div
					v-if="gameMinute"
					class="tag-preview__time"
				>
					{{ gameMinute }}’
				</div>
				<div
					v-else
					class="tag-preview__time"
				>
					{{ timeLabel }}
				</div>
			</div>

			<div class="tag-preview__info">
				{{ name }}
			</div>

			<button
				v-if="!isAnalysisMode"
				class="tag-preview__action-button"
				data-qa="preview-share"
				@click.stop="handleShare"
			>
				<Icon
					icon-name="network-communication"
					size="sm"
				/>
			</button>

			<template v-else>
				<button
					class="tag-preview__action-button"
					@click.stop="openContextMenu"
				>
					<Icon
						icon-name="context-menu"
						size="sm"
					/>
				</button>
				<div
					v-if="contextMenuOpened"
					class="tag-preview__action-button-w"
				>
					<button
						v-if="!noEdit"
						class="tag-preview__action-button"
						@click.stop="handleEdit"
					>
						<Icon
							icon-name="pen"
							size="sm"
						/>
					</button>
					<button
						class="tag-preview__action-button"
						data-qa="preview-share"
						@click.stop="handleShare"
					>
						<Icon
							icon-name="network-communication"
							size="sm"
						/>
					</button>
					<button
						class="tag-preview__action-button -danger"
						data-qa="preview-share"
						@click.stop="handleRemove"
					>
						<Icon
							icon-name="bin"
							size="sm"
						/>
					</button>
					<button
						class="tag-preview__action-button"
						@click.stop="closeContextMenu"
					>
						<Icon
							icon-name="e-remove"
							size="sm"
						/>
					</button>
				</div>
			</template>
		</div>
		<div
			v-visible="isPlaying && !previewFinished"
			class="tag-preview__play-progress"
		>
			<!-- Duplicate condition to keep the element space but reset the style to avoid ui errors in some cases -->
			<div
				v-if="isPlaying && !previewFinished"
				class="tag-preview__play-progress-bar"
				:style="playingCursorStyle"
			/>
		</div>
	</div>
</template>

<script>
import Icon from '@components/Icon';
import { mapGetters } from 'vuex';
import { formatTimestampAsMinutesSeconds } from '@shared/utils/timeUtils';
import { EVENT, bus } from '@videotools/js/events';

const events = [EVENT.PLAYER.ON.PROGRESS];

export default {
	name:       'TagPreview',
	components: { Icon },
	props:      {
		id: {
			type:    Number,
			default: -1,
		},
		name: {
			type:     String,
			required: true,
		},
		time: {
			type:     Number,
			required: true,
		},
		gameMinute: {
			type:     Number,
			required: false,
		},
		noEdit: {
			type: Boolean,
		},
		isInMulti: {
			type: Boolean,
		},
		secondsBefore: {
			type:    Number,
			default: 0,
		},
		secondsAfter: {
			type:    Number,
			default: 0,
		},
		sportName: {
			type: String,
		},
		isPlaying: {
			type: Boolean,
		},
	},
	data() {
		return {
			elapsed:           0,
			contextMenuOpened: false,
			internalElapsed:   0,
		};
	},
	computed: {
		...mapGetters({
			isAnalysisMode: 'app/isAnalysisMode',
		}),
		timeLabel() {
			return formatTimestampAsMinutesSeconds(this.time);
		},
		timeStart() {
			return this.time - this.secondsBefore || 0;
		},
		timeEnd() {
			return this.time + this.secondsAfter || 0;
		},
		previewFinished() {
			return this.timeStart >= this.internalElapsed || this.internalElapsed >= this.timeEnd;
		},
		playingCursorStyle() {
			const total = this.secondsBefore + this.secondsAfter;
			const val = this.internalElapsed - (this.time - this.secondsBefore);
			const width = (val * 100) / total;
			return { width: `${width}%` };
		},
	},
	watch: {
		elapsed(value) {
			this.internalElapsed = value;
		},
	},
	mounted() {
		this.internalElapsed = this.elapsed;

		// subscribe to external events
		for (const event of events) {
			bus.subscribe(event, this[`BUS-${event}`]);
		}
	},
	destroyed() {
		for (const event of events) {
			bus.unsubscribe(event, this[`BUS-${event}`]);
		}
	},
	methods: {
		[`BUS-${EVENT.PLAYER.ON.PROGRESS}`](val) {
			this.internalElapsed = val;
		},
		openContextMenu() {
			this.contextMenuOpened = true;
		},
		closeContextMenu() {
			this.contextMenuOpened = false;
		},
		handleMouseEnter(event) {
			this.$emit('mouseenter', event);
		},
		handleMouseLeave(event) {
			this.$emit('mouseleave', event);
		},
		handlePlayToggle() {
			if (!this.isPlaying || this.previewFinished) {
				this.handlePlay();
			} else {
				this.handlePause();
			}
		},
		handlePlay() {
			this.$emit('play', this.id);
		},
		handlePause() {
			this.$emit('pause', this.id);
		},
		handleEdit() {
			this.$emit('edit', this.id);
		},
		handleShare() {
			this.$emit('share', this.id);
		},
		handleRemove() {
			this.$emit('remove', this.id);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@videotools/sass/_fonts';
@import '@shared/sass/shared-variables';

.tag-preview {
	position: relative;

	&.-multi {
		position: relative;
		box-shadow: none;
	}
}

.tag-preview__item {
	position: relative;
	display: flex;
	gap: $sp3;
	color: var(--color-shadow-grey);
	padding: $sp2 0;
	cursor: pointer;
}

.tag-preview__time-play-w {
	width: $sp7;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;

	.tag-preview__item:hover & {
		color: var(--color-grey-600);
	}

	&.-game-minute {
		width: $sp6;
	}
}

.tag-preview__play-icon {
	display: none;

	.tag-preview__item:hover & {
		display: block;
	}
}

.tag-preview__time {
	@include text-sm-2;

	.tag-preview__item:hover & {
		display: none;
	}
}

.tag-preview__info {
	@include text-sm-2;
	font-weight: 500;
	flex-grow: 1;
	min-width: $sp7;
	max-width: 240px;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;

	.tag-preview__item:hover & {
		color: var(--color-grey-600);
	}
}

.tag-preview__action-button-w {
	position: absolute;
	right: 0;
	top: 0;
	background: var(--color-white);
	height: 100%;
	display: flex;
	align-items: center;
	gap: $sp3;
	z-index: 1;

	// add a gradient from white to transparent to the left
	&::before {
		content: '';
		position: absolute;
		left: -30px;
		top: 0;
		width: 30px;
		height: 100%;
		background: linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
		z-index: 2;
	}
}

.tag-preview__action-button {
	&.-danger {
		color: var(--color-red);
	}
}

.tag-preview__edit-overlay {
	position: absolute;
	display: flex;
	justify-items: center;
	align-items: center;
	justify-content: center;
	background: rgba(var(--color-rgb-shadow-grey), 0.96);
	width: 100%;
	height: 100%;
}

.tag-preview__play-progress {
	position: absolute;
	bottom: -1px;
	width: 100%;
	height: 2px;
	background: var(--color-grey-300);
}

.tag-preview__play-progress-bar {
	height: 2px;
	background: var(--color-hype-yellow);
	transition: width 0.25s linear; // here
}
</style>
